import { array, option, predicate, string } from "fp-ts"
import { constFalse, pipe } from "fp-ts/function"
import { Key, ReactNode } from "react"
import { apply, css, tw } from "twind/css"

interface ConditionalDetailsProps {
  getDetails: () => ReactNode
  getLabel: () => ReactNode
  open: boolean
  when: predicate.Predicate<void>
}

const markerCss = css`
  content: "";
  display: grid;
  position: absolute;
  top: calc(var(--spacing) / 2 - var(--radius));
  left: calc(var(--spacing) - var(--radius) - 1px);
  width: calc(2 * var(--radius));
  height: calc(2 * var(--radius));
  border-radius: 50%;
  background: #ddd;
`

const ConditionalDetails = ({
  getDetails,
  getLabel,
  open = false,
  when,
}: ConditionalDetailsProps) => {
  return when() ? (
    <details
      className={tw(
        css`
          &[open] > summary::before {
            content: "−";
          }
        `
      )}
      open={open}
    >
      <summary
        className={tw(
          `block`,
          css`
            &::marker,
            &::-webkit-details-marker {
              display: none;
            }

            &:focus {
              outline: none;
            }

            &:focus-visible {
              outline: 1px dotted #000;
            }

            &::before {
              ${markerCss}

              content: "+";
              z-index: 1;
              cursor: pointer;
              ${apply`bg-blue-300`}
              color: #fff;
              line-height: calc(2 * var(--radius) - 2px);
              text-align: center;
            }
          `
        )}
      >
        {getLabel()}
      </summary>
      {getDetails()}
    </details>
  ) : (
    <>
      {getLabel()}
      {getDetails()}
    </>
  )
}

interface SimpleTreeProps<T> {
  activeKey?: option.Option<Key>
  className?: string
  getChildren: (t: T) => T[]
  getKey: (t: T) => Key
  getLabel: (t: T) => ReactNode
  items: T[]
}

export function SimpleTree<T>({
  className,
  getChildren,
  getKey,
  getLabel,
  activeKey = option.none,
  items,
}: SimpleTreeProps<T>) {
  return (
    <ul
      className={tw(
        className,
        css`
          --spacing: 1.7rem;
          --radius: 10px;

          margin-left: calc(var(--radius) - var(--spacing));
          padding-left: 0;
        `
      )}
    >
      {pipe(
        items,
        array.map((item) => {
          const children = getChildren(item)

          const key = getKey(item)

          const isActive = pipe(
            activeKey,
            option.map(string.startsWith(String(key))),
            option.getOrElse(constFalse)
          )

          return (
            <li
              className={tw(
                `block relative`,
                `border(l-2 last:transparent)`, // vertical lines
                css`
                  padding-left: calc(2 * var(--spacing) - var(--radius) - 2px);
                  line-height: var(--spacing);

                  &::before {
                    content: "";
                    display: block;
                    position: absolute;
                    top: calc(var(--spacing) / -2);
                    left: -2px;
                    width: calc(var(--spacing) + 2px);
                    height: calc(var(--spacing) + 1px);
                    border: solid #ddd;
                    border-width: 0 0 2px 2px;
                  }

                  &::after {
                    ${markerCss}
                  }
                `
              )}
              key={getKey(item)}
            >
              <ConditionalDetails
                getDetails={() => (
                  <SimpleTree
                    activeKey={activeKey}
                    getChildren={getChildren}
                    getKey={getKey}
                    getLabel={getLabel}
                    items={children}
                  />
                )}
                getLabel={() => getLabel(item)}
                open={isActive}
                when={() => array.isNonEmpty(children)}
              />
            </li>
          )
        })
      )}
    </ul>
  )
}
